import {
  type AwgSize,
  type CalcResult,
  type ConductorMaterial,
  err,
  isAwgSize,
  isPositiveFinite,
  ok,
} from "../types.ts";
import { dcResistanceOhmPerKft } from "../tables/conductors.ts";

export type VoltageDropInput = {
  currentA: number;
  lengthFt: number; // one-way run length
  awg: AwgSize;
  material: ConductorMaterial;
  phase: "single" | "three";
  sourceVolts: number;
};

export type VoltageDropResult = {
  voltageDropV: number;
  percentDrop: number;
  endVoltageV: number; // voltage remaining at the load
  conductorResistanceOhm: number; // total resistance of the run (one-way)
};

/**
 * Voltage drop via NEC Chapter 9 Table 8 DC conductor resistance.
 *
 *   single phase:  Vdrop = 2 · I · R_perFt · L
 *   three phase:   Vdrop = √3 · I · R_perFt · L
 *
 * The factor of 2 (single-phase) accounts for the out-and-back conductor
 * length; √3 is the line-to-line three-phase factor. R comes from Table 8 at
 * 75°C, the column nearly every published worked example uses.
 *
 * v1 limitation (documented, deferred to v1.1): this uses DC resistance and
 * does not add conductor reactance from Table 9. For typical branch-circuit
 * and feeder sizing this matches published Ugly's examples; large conductors
 * at low power factor will read slightly low. The UI labels results
 * accordingly.
 */
export function voltageDrop(input: VoltageDropInput): CalcResult<VoltageDropResult> {
  const { currentA, lengthFt, awg, material, phase, sourceVolts } = input;

  if (!isAwgSize(awg)) {
    return err(`Unsupported conductor size "${String(awg)}".`);
  }
  if (material !== "copper" && material !== "aluminum") {
    return err(`Material must be "copper" or "aluminum".`);
  }
  if (phase !== "single" && phase !== "three") {
    return err(`Phase must be "single" or "three".`);
  }
  if (!isPositiveFinite(currentA)) {
    return err("Current (A) must be a positive number.");
  }
  if (!isPositiveFinite(lengthFt)) {
    return err("Length (ft) must be a positive number.");
  }
  if (!isPositiveFinite(sourceVolts)) {
    return err("Source voltage (V) must be a positive number.");
  }

  const resistancePerFt = dcResistanceOhmPerKft(awg, material) / 1000;
  const conductorResistanceOhm = resistancePerFt * lengthFt;
  const phaseFactor = phase === "single" ? 2 : Math.sqrt(3);

  const voltageDropV = phaseFactor * currentA * conductorResistanceOhm;
  const percentDrop = (voltageDropV / sourceVolts) * 100;
  const endVoltageV = sourceVolts - voltageDropV;

  return ok({
    voltageDropV,
    percentDrop,
    endVoltageV,
    conductorResistanceOhm,
  });
}
